/**
 * Admin API Service
 *
 * API client for admin console operations.
 * Uses the existing apiClient for authenticated requests.
 */

import { fetchAuthSession } from 'aws-amplify/auth';

const API_URL = import.meta.env.VITE_API_URL;

if (!API_URL) {
  throw new Error('VITE_API_URL environment variable is not set');
}

interface ApiError {
  error: string;
  message: string;
}

class AdminApiClient {
  private async getAuthToken(): Promise<string | null> {
    try {
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken?.toString();
      return idToken || null;
    } catch (error) {
      console.error('Failed to get auth token:', error);
      return null;
    }
  }

  private async request<T>(path: string, options: RequestInit = {}): Promise<T> {
    const token = await this.getAuthToken();

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    if (options.headers) {
      Object.assign(headers, options.headers);
    }

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`${API_URL}${path}`, {
      ...options,
      headers,
    });

    if (!response.ok) {
      let errorData: ApiError;
      try {
        errorData = await response.json();
      } catch {
        errorData = {
          error: 'Unknown Error',
          message: `HTTP ${response.status}: ${response.statusText}`,
        };
      }
      throw new Error(errorData.message || 'Request failed');
    }

    // Handle 204 No Content responses (empty body)
    if (response.status === 204) {
      return undefined as T;
    }

    return response.json();
  }

  // Stats
  async getStats(): Promise<{
    users: number;
    tournaments: number;
    leagues: number;
    leagueMembers: number;
    wrestlers: number;
    activeScraperRuns: number;
    recentScraperFailures: number;
  }> {
    return this.request('/admin/stats');
  }

  // Global search
  async globalSearch(query: string): Promise<{
    users: any[];
    tournaments: any[];
    leagues: any[];
    wrestlers: any[];
  }> {
    return this.request(`/admin/search?q=${encodeURIComponent(query)}`);
  }

  // Entity CRUD
  async listEntities(
    table: string,
    params: {
      limit?: number;
      offset?: number;
      sort?: string;
      order?: 'asc' | 'desc';
      filter_column?: string;
      filter_value?: string;
      search?: string;
    } = {}
  ): Promise<{
    rows: any[];
    total: number;
    limit: number;
    offset: number;
  }> {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        queryParams.append(key, value.toString());
      }
    });

    const queryString = queryParams.toString();
    const path = `/admin/entities/${table}${queryString ? `?${queryString}` : ''}`;

    return this.request(path);
  }

  async getEntity(table: string, id: string): Promise<any> {
    return this.request(`/admin/entities/${table}/${id}`);
  }

  async updateEntity(table: string, id: string, updates: Record<string, any>): Promise<any> {
    return this.request(`/admin/entities/${table}/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(updates),
    });
  }

  async deleteEntity(table: string, id: string): Promise<void> {
    return this.request(`/admin/entities/${table}/${id}`, {
      method: 'DELETE',
    });
  }

  // Scraper runs
  async listScraperRuns(
    params: {
      limit?: number;
      offset?: number;
      tournament_id?: string;
      status?: string;
    } = {}
  ): Promise<{
    runs: any[];
    total: number;
    limit: number;
    offset: number;
  }> {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        queryParams.append(key, value.toString());
      }
    });

    const queryString = queryParams.toString();
    const path = `/admin/scraper-runs${queryString ? `?${queryString}` : ''}`;

    return this.request(path);
  }

  async getScraperRun(id: string): Promise<any> {
    return this.request(`/admin/scraper-runs/${id}`);
  }

  async getScraperRunLogUrl(id: string): Promise<{ url: string; expiresIn: number }> {
    return this.request(`/admin/scraper-runs/${id}/log`);
  }

  async triggerScraperRun(
    tournamentId: string
  ): Promise<{ message: string; tournamentId: string }> {
    return this.request('/admin/scraper-runs/trigger', {
      method: 'POST',
      body: JSON.stringify({ tournamentId }),
    });
  }

  // Wrestler Duplicates
  async getTournamentsWithDuplicates(): Promise<{
    tournaments: { id: string; name: string; duplicate_count: number }[];
  }> {
    return this.request('/admin/wrestler-duplicates/tournaments');
  }

  async getDuplicateWrestlers(tournamentId?: string): Promise<{
    groups: {
      tournament_id: string;
      tournament_name: string;
      school_team: string;
      weight_class: string;
      wrestlers: {
        id: string;
        name: string;
        seed: number | null;
        seed_source: string | null;
        place: number | null;
        team_points: number;
        status: string;
        roster_count: number;
      }[];
    }[];
    total: number;
  }> {
    const params = tournamentId ? `?tournament_id=${tournamentId}` : '';
    return this.request(`/admin/wrestler-duplicates${params}`);
  }

  async mergeWrestlers(
    keepWrestlerId: string,
    removeWrestlerId: string
  ): Promise<{
    rostersUpdated: number;
    rostersSkipped: number;
    membersRecalculated: number;
    removedWrestler: { id: string; name: string };
    keptWrestler: { id: string; name: string };
  }> {
    return this.request('/admin/wrestler-duplicates/merge', {
      method: 'POST',
      body: JSON.stringify({ keepWrestlerId, removeWrestlerId }),
    });
  }
  // Broadcasts
  async listBroadcasts(): Promise<{
    broadcasts: {
      id: string;
      senderDisplayName: string | null;
      title: string;
      content: string;
      broadcastType: string;
      priority: number;
      isPublished: boolean;
      publishedAt: string | null;
      expiresAt: string | null;
      createdAt: string;
      updatedAt: string;
    }[];
  }> {
    return this.request('/admin/broadcasts');
  }

  async createBroadcast(input: {
    title: string;
    content: string;
    broadcastType?: string;
    priority?: number;
    expiresAt?: string | null;
  }): Promise<any> {
    return this.request('/admin/broadcasts', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  }

  async updateBroadcast(
    id: string,
    input: {
      title?: string;
      content?: string;
      broadcastType?: string;
      priority?: number;
      expiresAt?: string | null;
    }
  ): Promise<any> {
    return this.request(`/admin/broadcasts/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    });
  }

  async publishBroadcast(id: string, publish: boolean): Promise<any> {
    return this.request(`/admin/broadcasts/${id}/publish`, {
      method: 'POST',
      body: JSON.stringify({ publish }),
    });
  }

  async deleteBroadcast(id: string): Promise<void> {
    return this.request(`/admin/broadcasts/${id}`, {
      method: 'DELETE',
    });
  }

  // Brevo sync
  async syncAllToBrevo(): Promise<{
    total: number;
    synced: number;
    failed: number;
    errors: string[];
  }> {
    return this.request('/admin/brevo-sync', {
      method: 'POST',
    });
  }
}

export const adminApi = new AdminApiClient();
